/* eslint-disable react/button-has-type */
import classNames from 'classnames';
import { MouseEvent, ReactNode } from 'react';
import { Link } from 'wouter';

import styles from '../../../styles/buttons/button.module.css';

interface Message {
    id: string;
    defaultMessage?: string;
}

type Label = Message | ReactNode;

interface ButtonProps {
    text?: string | null;
    type?: 'button' | 'submit' | 'reset';
    href?: string | null;
    external?: boolean;
    direct?: boolean;
    target?: string;
    label?: Label | null;
    children?: Label | null;
    icon?: ReactNode | null;
    iconPosition?: 'left' | 'right' | 'inline';
    disabled?: boolean;
    loading?: boolean;
    disableOnLoading?: boolean;
    transparent?: boolean;
    red?: boolean;
    small?: boolean;
    outline?: boolean;
    white?: boolean;
    withShadow?: boolean;
    className?: string | null;
    iconClassName?: string | null;
    labelClassName?: string | null;
    onClick?: ((e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void) | null;
    [key: `aria-${string}`]: string | boolean | undefined;
}

function Button({
    text = null,
    type = 'button',
    href = null,
    external = false,
    direct = false,
    target = '_blank',
    label = null,
    children = null,
    icon = null,
    iconPosition = 'inline',
    disabled = false,
    loading = false,
    disableOnLoading = true,
    transparent = false,
    red = false,
    small = false,
    outline = false,
    white = false,
    withShadow = false,
    className = null,
    iconClassName = null,
    labelClassName = null,
    onClick = null,
    ...props
}: ButtonProps) {
    const finalLabel = label;
    // Only ARIA attributes reach the DOM: callers also pass props meant for other buttons (color…)
    const ariaProps = Object.fromEntries(
        Object.entries(props).filter(([key]) => key.startsWith('aria-')),
    );

    const hasChildren = children !== null;
    const hasIcon = icon !== null;
    const hasInlineIcon = hasIcon && (iconPosition === 'inline' || text === null);
    const hasIconColumns = hasIcon && !hasInlineIcon;

    const content = (
        <>
            {hasInlineIcon ? (
                <>
                    <span
                        className={classNames([
                            styles.icon,
                            {
                                [iconClassName]: iconClassName !== null,
                            },
                        ])}
                    >
                        {icon}
                    </span>
                    {text !== null ? (
                        <span
                            className={classNames([
                                styles.label,
                                {
                                    [labelClassName]: labelClassName !== null,
                                },
                            ])}
                        >
                            {text}
                        </span>
                    ) : null}
                </>
            ) : null}
            {hasIconColumns ? (
                <>
                    <span className={classNames([styles.left])}>
                        {iconPosition === 'left' ? icon : null}
                    </span>
                    <span className={classNames([styles.center])}>{text}</span>
                    <span className={classNames([styles.right])}>
                        {iconPosition === 'right' ? icon : null}
                    </span>
                    {hasChildren ? children : null}
                </>
            ) : null}
            {!hasIcon ? finalLabel : null}
            {hasChildren ? children : null}
        </>
    );

    const buttonClassNames = classNames([
        styles.container,
        {
            [styles.transparent]: transparent,
            [styles.red]: red,
            [styles.small]: small,
            [styles.outline]: outline,
            [styles.white]: white,
            [styles.withShadow]: withShadow,
            [styles.withIcon]: hasIcon,
            [styles.withIconColumns]: hasIconColumns,
            [styles.withText]: text !== null,
            [styles.isLink]: href !== null,
            [styles.isDisabled]: disabled,
            [styles.isLoading]: loading,
            [className]: className !== null,
        },
    ]);

    if (href !== null) {
        return external || direct ? (
            <a
                href={href}
                className={buttonClassNames}
                onClick={onClick}
                target={external ? target : null}
                {...ariaProps}
            >
                {content}
            </a>
        ) : (
            <Link href={href} className={buttonClassNames} onClick={onClick} {...ariaProps}>
                {content}
            </Link>
        );
    }
    return (
        <button
            type={type}
            className={buttonClassNames}
            onClick={onClick}
            disabled={disabled || (disableOnLoading && loading)}
            {...ariaProps}
        >
            {content}
        </button>
    );
}

export default Button;
